import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { CircleHelp, Loader2, X } from "lucide-react";
import { DETAILED_HELP_CONTENT } from "./detailedHelpContent";
import type { DetailedHelpItem } from "./help-content/types";
import { useHelpScopeStore } from "@/store/helpScope.store";
import { useHelpContextQuery } from "@/hooks/api/use-help-context-query";
import { cn } from "@/lib/utils";

interface DetailedHelpPanelProps {
  open: boolean;
  onClose: () => void;
}

const HIGHLIGHT_CLASS = "innsight-help-highlight";

/**
 * Lê a tela de verdade: quais elementos com `data-help-key` estão montados
 * agora. Itens da ajuda que apontam pra um campo inexistente (escondido por
 * permissão, plano, aba fechada) somem — a ajuda nunca descreve algo que
 * este usuário não está vendo. Ignora o que estiver dentro do próprio painel.
 */
function useRenderedHelpKeys(open: boolean): Set<string> {
  const [keys, setKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!open) return;

    const scan = () => {
      const found = new Set<string>();
      document.querySelectorAll<HTMLElement>("[data-help-key]").forEach((element) => {
        if (element.closest("[data-help-panel]")) return;
        const key = element.dataset.helpKey;
        if (key) found.add(key);
      });
      setKeys(found);
    };

    scan();
    // A tela pode mudar com o painel aberto (abrir um accordion, carregar
    // dados). Reagir a isso mantém a lista honesta sem precisar reabrir.
    const observer = new MutationObserver(scan);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open]);

  return keys;
}

export function DetailedHelpPanel({ open, onClose }: DetailedHelpPanelProps) {
  const { pathname } = useLocation();
  const scope = useHelpScopeStore((state) => state.scope);
  const renderedKeys = useRenderedHelpKeys(open);
  const { data: helpContext, isLoading: contextLoading } = useHelpContextQuery(open, pathname);

  const sections =
    (scope ? DETAILED_HELP_CONTENT[`${pathname}#${scope}`] : undefined) ?? DETAILED_HELP_CONTENT[pathname];

  /**
   * Um item com `domKey` só entra se o campo estiver na tela. Sem `domKey`,
   * o item é conceitual (explica a tela, não um campo) e sempre aparece.
   */
  const visibleSections = useMemo(() => {
    if (!sections) return undefined;
    return sections
      .map((section) => ({
        ...section,
        items: section.items?.filter((item) => !item.domKey || renderedKeys.has(item.domKey)),
      }))
      .filter((section) => section.description || (section.items && section.items.length > 0));
  }, [sections, renderedKeys]);

  const highlight = useCallback((domKey: string | undefined, on: boolean) => {
    if (!domKey) return;
    document.querySelectorAll<HTMLElement>(`[data-help-key="${CSS.escape(domKey)}"]`).forEach((element) => {
      if (element.closest("[data-help-panel]")) return;
      element.classList.toggle(HIGHLIGHT_CLASS, on);
      if (on) element.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Nenhum destaque pode sobreviver ao fechamento do painel.
  useEffect(() => {
    if (open) return;
    document
      .querySelectorAll<HTMLElement>(`.${HIGHLIGHT_CLASS}`)
      .forEach((element) => element.classList.remove(HIGHLIGHT_CLASS));
  }, [open]);

  if (!open) return null;

  const renderItem = (item: DetailedHelpItem) => {
    const liveFact = helpContext && item.fact ? item.fact(helpContext) : null;
    const VisualIcon = item.visual?.icon;

    return (
      <div
        key={item.label}
        onMouseEnter={() => highlight(item.domKey, true)}
        onMouseLeave={() => highlight(item.domKey, false)}
        className={item.domKey ? "cursor-help rounded-lg -mx-1.5 px-1.5 py-1 transition-colors hover:bg-muted/40" : undefined}
      >
        <dt className="text-xs font-semibold text-foreground">{item.label}</dt>
        {item.visual ? (
          <dd className="mt-1.5">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-semibold",
                item.visual.tone === "primary" && "border-primary/40 bg-primary/15 text-primary",
                item.visual.tone === "danger" && "border-[hsl(var(--danger-border))]/40 bg-[hsl(var(--danger-border))]/10 text-[hsl(var(--danger-border))]",
                (!item.visual.tone || item.visual.tone === "outline") && "border-border bg-muted/40 text-foreground",
              )}
            >
              {VisualIcon ? <VisualIcon className="h-3.5 w-3.5" aria-hidden /> : null}
              {item.visual.text}
            </span>
            <span className="ml-2 text-xs text-muted-foreground">é assim que aparece na tela</span>
          </dd>
        ) : null}
        <dd className="mt-1 text-xs leading-5 text-muted-foreground">{item.explanation}</dd>
        {liveFact ? (
          <dd className="mt-1.5 rounded-md border border-primary/25 bg-primary/5 px-2 py-1 text-xs leading-5 text-foreground">
            <span className="font-semibold text-primary">Agora nesta pousada: </span>
            {liveFact}
          </dd>
        ) : null}
      </div>
    );
  };

  // Portal pro body de proposito: o painel é montado dentro do cabeçalho, que
  // tem backdrop-blur e z-index próprios. `backdrop-filter` vira containing
  // block de `position: fixed`, então sem o portal o `inset-0` resolvia contra
  // a faixa de 70px do cabeçalho em vez da viewport — e o z-index do painel
  // ficava preso no contexto de empilhamento dele.
  return createPortal(
    <div
      data-help-panel
      className="fixed inset-0 z-[100] flex justify-end bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Ajuda detalhada desta tela"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="glass flex h-full w-full max-w-md flex-col border-l border-border p-5">
        <div className="flex items-center justify-between gap-3 border-b border-border/60 pb-4">
          <div className="flex items-center gap-2">
            <CircleHelp className="h-5 w-5 text-primary" />
            <h2 className="text-base font-bold text-foreground">Ajuda detalhada desta tela</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar ajuda detalhada"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {contextLoading ? (
          <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Consultando a configuração real desta pousada...
          </p>
        ) : null}

        <div className="mt-4 flex-1 space-y-6 overflow-y-auto pb-2">
          {!visibleSections || visibleSections.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
              A ajuda detalhada desta tela ainda não foi escrita. Volte em breve — estamos completando tela por tela.
            </p>
          ) : (
            visibleSections.map((section) => (
              <div key={section.title}>
                <h3 className="text-sm font-bold text-foreground">{section.title}</h3>
                {section.description ? (
                  <p className="mt-1.5 text-xs leading-5 text-muted-foreground">{section.description}</p>
                ) : null}
                {section.items && section.items.length > 0 ? (
                  <dl className="mt-3 space-y-2.5 border-l-2 border-border/60 pl-3">
                    {section.items.map(renderItem)}
                  </dl>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
